import { useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MultiSelect } from '@mantine/core';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { useDebouncedCallback } from '@mantine/hooks';
import { usePaginatedQuery } from '../../../http/queries/usePaginatedQuery';

export const CustomMultiSelect = ({
    optionsMapFn,
    queryHttpFn,
    queryKey,
    filters,
    cacheKeyFn = (x) => x.id,
    pageSize = 100,
    renderOption,
    refetchInterval = 5_000,
    onChange,
    defaultValue,
    ...inputProps
}) => {
    const [searchValue, setSearchValue] = useState('');
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setSearchValue(query);
    }, 600);

    const [selectedQueryData, setSelectedQueryData] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => queryHttpFn(queryParams), //getAvailableAgents(queryParams),
        queryKey: [queryKey],
        refetchInterval: refetchInterval,
        globalFilter: searchValue,
        filters,
        pagination,
        sorting: [],
    });

    const { data: queryData, pagination: dataPagination } = paginatedData || {};

    const selectOptions = useMemo(() => {
        const currentOptions = queryData ? queryData.map(optionsMapFn) : [];

        const missingSelected = selectedOptions.filter(
            (x) => !currentOptions.some((x2) => x2.value == x.value)
        );

        const missingDefault = defaultValue
            ? defaultValue.filter(
                  (x) => !currentOptions.some((x2) => x2.value == x.value)
              )
            : [];

        return [...missingDefault, ...missingSelected, ...currentOptions];
    }, [queryData, defaultValue, isLoading]);

    const handleSelectChange = (values) => {
        console.log(values);
        const missingSelected = values.filter(
            (x) => !selectedOptions.some((x2) => x2.value == x)
        );

        const newShit = missingSelected
            .map((x) => selectOptions.find((x2) => x2.value == x))
            .filter((x) => x != null);

        const merged = [...selectedOptions, ...newShit].filter(
            (x) => x != null
        );
        const filteredOptions = merged.filter((x) =>
            values.some((value) => x.value === value)
        );

        const missingQueryData = filteredOptions
            .map((x) => x.value)
            .filter(
                (value) =>
                    !selectedQueryData.some(
                        (queryData) => cacheKeyFn(queryData) != value
                    )
            )
            .map((value) =>
                queryData.find((queryData) => cacheKeyFn(queryData) == value)
            )
            .filter((x) => x != null);

        const queryDataMerged = [
            ...selectedQueryData,
            ...missingQueryData,
        ].filter((x) => x != null);

        setSelectedOptions(filteredOptions);
        if (queryDataMerged) {
            setSelectedQueryData(queryDataMerged);
        }
        onChange(filteredOptions.map((x) => x.value));
    };

    const internalRenderMultiSelectOption = useMemo(
        () =>
            ({ option, ...rest }) => {
                let obj = queryData.find((x) => cacheKeyFn(x) == option.value);
                if (!obj) {
                    obj = selectedQueryData.find(
                        (x) => cacheKeyFn(x) == option.value
                    );
                }
                return renderOption({
                    option: option,
                    queryData: obj,
                    ...rest,
                });
            },
        [queryData, selectedQueryData]
    );

    return (
        <MultiSelect
            searchable
            onSearchChange={filterDebounce}
            rightSection={
                isLoading ? (
                    <FontAwesomeIcon
                        icon={faSpinnerThird}
                        spin
                    />
                ) : null
            }
            data={selectOptions}
            renderOption={renderOption ? internalRenderMultiSelectOption : null}
            defaultValue={defaultValue}
            onChange={handleSelectChange}
            {...inputProps}
        />
    );
};
export default CustomMultiSelect;
